/**
 * API Configuration
 * 
 * Reads from environment variables in .env file:
 * - VITE_API_BASE_URL: Backend API base URL (required)
 * - VITE_API_TIMEOUT: API request timeout in milliseconds (optional, default: 90000)
 * - VITE_HEAVY_MUTATION_TIMEOUT_MS: create card / claim payment / create payment (optional, default: 120000)
 * - VITE_IRR_ELIGIBLE_TIMEOUT_MS: IRR eligible-installments POST only (optional, default: 300000 = 5 min)
 * 
 * Environment files are loaded by Vite automatically:
 * - .env.local (highest priority, not committed to git)
 * - .env.development (development mode)
 * - .env.production (production mode)
 * - .env (lowest priority)
 * 
 * IMPORTANT: Make sure to create a .env file in the project root with:
 *   VITE_API_BASE_URL=http://your-api-url/api/v1
 */

// Get API base URL from environment variable
const getApiBaseUrl = (): string => {
  // Vite automatically loads .env files and exposes variables via import.meta.env
  // Variables must be prefixed with VITE_ to be exposed to the client
  let envUrl = import.meta.env.VITE_API_BASE_URL;
  
  if (!envUrl || envUrl.trim() === '') {
    // const fallbackUrl = 'https://api.zeettu.payzhe.fit/api/v1';
    const fallbackUrl = 'http://localhost:3011/api/v1';
    
    // Warn in development if env variable is not set
    if (import.meta.env.DEV) {
      console.warn(
        '⚠️ VITE_API_BASE_URL is not set in .env file.\n' +
        `Using fallback URL: ${fallbackUrl}\n` +
        'To fix: Create a .env file in the project root with:\n' +
        '  VITE_API_BASE_URL=http://api.zeettu.payzhe.fit/api/v1\n' +
        'Or copy env.example to .env and update the values.'
      );
    }
    
    return fallbackUrl;
  }
  
  // Normalize the URL: fix localhost to use http:// instead of https://
  // This prevents SSL errors when connecting to local development servers
  envUrl = envUrl.trim();
  if (envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
    // Replace https:// with http:// for localhost URLs
    envUrl = envUrl.replace(/^https:\/\/(localhost|127\.0\.0\.1)/, 'http://$1');
    
    if (import.meta.env.DEV) {
      console.log(`🔧 Normalized localhost URL to use http://: ${envUrl}`);
    }
  }
  
  // Log the API URL being used (only in development)
  if (import.meta.env.DEV) {
    console.log(`✅ Using API URL: ${envUrl}`);
  }
  
  return envUrl;
};

const API_BASE_URL = getApiBaseUrl();

export const API_TIMEOUT = Number.parseInt(
  import.meta.env.VITE_API_TIMEOUT || '90000',
  10,
);

/** Create installment card, claim payment, create payment — longer client wait (default 2 minutes). */
export const HEAVY_MUTATION_TIMEOUT_MS =
  Number.parseInt(import.meta.env.VITE_HEAVY_MUTATION_TIMEOUT_MS || '1200000', 10) || 120000;

/** IRR eligible-installments aggregation can take a long time; client waits up to this (default 5 minutes). */
export const IRR_ELIGIBLE_INSTALLMENTS_TIMEOUT_MS =
  Number.parseInt(import.meta.env.VITE_IRR_ELIGIBLE_TIMEOUT_MS || '3000000', 10) || 300000;

export default API_BASE_URL;
